import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api/client';
import type { LoyaltyReward, LoyaltyRewardInput } from './types';

/**
 * Loyalty rewards — what points can be spent on.
 *
 * All three writes answer with the whole updated list, so each one seeds the
 * cache directly instead of invalidating and fetching the same rows again.
 *
 * `LoyaltyService` checks roles on every one of them — SuperAdmin or Marketing,
 * refused otherwise — which makes this the one marketing feature whose UI gate
 * is actually backed by the API.
 */

const KEY = ['loyalty', 'rewards'] as const;

export function useLoyaltyRewards() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => get<LoyaltyReward[]>('loyalty/rewards'),
    select: (data) =>
      [...(data ?? [])].sort(
        (a, b) =>
          a.ordering - b.ordering ||
          (a.internalName ?? '').localeCompare(b.internalName ?? '')
      ),
    staleTime: 60_000,
  });
}

/** Every write returns the new list; hand it straight to the cache. */
function useSeedFromResponse() {
  const queryClient = useQueryClient();
  return (rewards: LoyaltyReward[] | null) => {
    if (rewards) queryClient.setQueryData(KEY, rewards);
    else void queryClient.invalidateQueries({ queryKey: KEY });
  };
}

export function useSaveReward() {
  const seed = useSeedFromResponse();

  return useMutation({
    mutationFn: (values: LoyaltyRewardInput) =>
      post<LoyaltyReward[]>('loyalty/rewards', values),
    onSuccess: seed,
  });
}

/**
 * Remove one.
 *
 * A POST to `rewards/{id}` rather than a DELETE — the only removal on this
 * controller and it does not follow the shape of the others.
 */
export function useRemoveReward() {
  const seed = useSeedFromResponse();

  return useMutation({
    mutationFn: (id: string) =>
      post<LoyaltyReward[]>(`loyalty/rewards/${id}`, {}),
    onSuccess: seed,
  });
}
